/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Else, If, Then } from "react-if";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Button, FlexContainer, Loader, theme } from "@nordcloud/gnui";
import {
  CloudProviderType,
  Maybe,
  Provider,
  useAddSyntheticEstateRecordMutation,
  useLinkedRecordsMutation,
} from "~/generated/graphql";
import { CloudResourcesMultiInput } from "~/components/Inputs/CloudResourcesMultiInput";
import { ERROR_TEXT } from "~/constants";
import { showError, showSuccess } from "~/services/toast";
import {
  generateActionSuccessText,
  getFirstItem,
  isCloudResource,
  isEmpty,
  isNotNil,
} from "~/tools";
import {
  isStorageInstance,
  isVMInstance,
} from "~/views/estate/EstateDetailsPage/utils";
import { CloudResource } from "~/views/estate/types";
import { LinkEditButton } from "./LinkEditButton";

type LinkedResource = {
  __typename?: "Link";
  sourceRecord: CloudResource | { __typename?: "CostResource" };
  targetRecord: CloudResource | { __typename?: "CostResource" };
};

type Props = {
  nordcloudId: string;
  provider?: Provider | "";
  providerType?: string | null;
  name?: string;
  providerId?: string;
  linkedResources?: LinkedResource[];
  onChange: () => void;
};

export function InlineCloudResourcesMultiLink({
  nordcloudId,
  provider,
  providerType,
  name,
  providerId,
  linkedResources,
  onChange,
}: Props) {
  const [isEditing, setEditing] = React.useState(false);
  const [cloudResources, setCloudResources] = React.useState<CloudResource[]>();
  const [updating, setUpdating] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);

  const [linkedRecords] = useLinkedRecordsMutation();
  const [addSyntheticEstateRecord] = useAddSyntheticEstateRecordMutation();

  const callOnChangeAndShowMessage = (message: string) => {
    onChange();
    setUpdating(false);
    showSuccess(message);
  };

  const records = React.useMemo(
    () => getTargetCloudResources(nordcloudId ?? "", linkedResources),
    [nordcloudId, provider, linkedResources]
  );

  const onEdit = async () => {
    closeEditing();
    try {
      setUpdating(true);

      const unlink = (records ?? []).map(({ id }) => ({
        sourceNid: nordcloudId ?? "",
        targetNid: id,
      }));

      const link = (cloudResources ?? []).map(({ id }) => ({
        sourceNid: nordcloudId ?? "",
        targetNid: id,
      }));

      await linkedRecords({
        variables: {
          link,
          unlink,
        },
      });

      callOnChangeAndShowMessage(
        generateActionSuccessText("Links")()(
          `${records ? "updated" : "created"}`
        )()
      );
    } catch {
      setUpdating(false);
      showError(ERROR_TEXT.default);
    }
  };

  const onDelete = async () => {
    closeEditing();
    try {
      setUpdating(true);

      const unlink = (records ?? []).map(({ id }) => ({
        sourceNid: nordcloudId ?? "",
        targetNid: id,
      }));

      await linkedRecords({
        variables: { unlink },
      });

      callOnChangeAndShowMessage(
        generateActionSuccessText("Link")()("deleted")()
      );
    } catch {
      setUpdating(false);
      showError(ERROR_TEXT.default);
    }
  };

  const closeEditing = () => {
    setEditing(false);
    setIsDirty(false);
    setCloudResources([]);
  };

  const openEditMode = () => {
    if (records) {
      setCloudResources(records);
    }
    setEditing(true);
  };

  const isVMRecord = isVMInstance(providerType ?? "");
  const isStorageRecord = isStorageInstance(providerType ?? "");

  const searchFilter = {
    provider: [Provider.Kubernetes, Provider.Openshift],
    ...getSearchFilter(isVMRecord, isStorageRecord),
  };

  const onCreateSyntheticRecord = async (
    cloudAccount: string,
    accProvider?: Maybe<CloudProviderType>
  ) => {
    setUpdating(true);
    try {
      const { data } = await addSyntheticEstateRecord({
        variables: {
          input: {
            provider:
              accProvider === CloudProviderType.Openshift
                ? Provider.Openshift
                : Provider.Kubernetes,
            providerId: name ?? "",
            subtype: providerType ?? "",
            cloudAccount,
            resourceId: providerId ?? "",
          },
        },
      });

      await linkedRecords({
        variables: {
          link: [
            {
              sourceNid: nordcloudId,
              targetNid: data?.addSyntheticEstateRecord?.id ?? "",
            },
          ],
        },
      });

      callOnChangeAndShowMessage(
        generateActionSuccessText("Links")()(
          `${records ? "updated" : "created"}`
        )()
      );

      closeEditing();
    } catch {
      setUpdating(false);
      showError(ERROR_TEXT.default);
    }
  };

  const onCloudResourcesChange = (resources: CloudResource[]) => {
    setCloudResources(resources);
    setIsDirty(true);
  };

  if (updating) {
    return <Loader inContent />;
  }

  return (
    <If condition={isEditing}>
      <Then>
        <EditForm>
          <FlexContainer>
            <CloudResourcesMultiInput
              small
              cloudResources={cloudResources}
              showCreate={!isVMRecord && !isStorageRecord}
              searchFilters={searchFilter}
              onChange={onCloudResourcesChange}
              onCreate={onCreateSyntheticRecord}
            />
            <If condition={isDirty || !records}>
              <Then>
                <Button
                  ml={theme.spacing.spacing02}
                  type="button"
                  icon="save"
                  size="md"
                  severity="low"
                  disabled={!isDirty}
                  onClick={onEdit}
                >
                  Save
                </Button>
              </Then>
              <Else>
                <Button
                  ml={theme.spacing.spacing02}
                  type="button"
                  icon="trash"
                  size="md"
                  severity="low"
                  onClick={onDelete}
                >
                  Delete
                </Button>
              </Else>
            </If>
            <Button
              ml={theme.spacing.spacing02}
              icon="close"
              size="md"
              severity="low"
              type="button"
              onClick={closeEditing}
            >
              Cancel
            </Button>
          </FlexContainer>
        </EditForm>
      </Then>
      <Else>
        <FlexContainer alignItems="center">
          <If condition={records.length === 1}>
            <Then>
              <Link
                data-testid={`link-${getFirstItem(records)?.id}`}
                to={`/estate/${getFirstItem(records)?.id}`}
              >
                {getFirstRecordName(records)}
              </Link>
            </Then>
            <Else>
              <If condition={records.length > 1}>
                <Then>
                  <>{records?.length} Resources</>
                </Then>
              </If>
            </Else>
          </If>
          <LinkEditButton onClick={openEditMode} />
        </FlexContainer>
      </Else>
    </If>
  );
}

const EditForm = styled.form`
  display: block;
  width: 100%;
`;

function getFirstRecordName(records: CloudResource[]) {
  const record = getFirstItem(records);

  if (isEmpty(record?.name ?? "")) {
    return record?.id;
  }

  return record?.name;
}

function getSearchFilter(isVMRecord: boolean, isStorageRecord: boolean) {
  if (isVMRecord) {
    return { typeSubtype: ["KUBERNETES/Node", "OPENSHIFT/Node"] };
  }

  if (isStorageRecord) {
    return {
      typeSubtype: [
        "KUBERNETES/PersistentVolume",
        "OPENSHIFT/PersistentVolume",
      ],
    };
  }

  return { type: ["synthetic"] };
}

function getTargetCloudResources(
  id: string,
  items: LinkedResource[] = []
): CloudResource[] {
  const links = items.filter(
    ({ sourceRecord }) =>
      isCloudResource(sourceRecord) && sourceRecord.id === id
  );

  return links
    .map(({ targetRecord }) => {
      return isCloudResource(targetRecord) ? targetRecord : undefined;
    })
    .filter(isNotNil);
}
